'use client';

import React, { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { ArrowUpDown, CirclePercent } from 'lucide-react';
import useNavigation from '@/src/lib/hooks/useNavigation';
import { cn } from '@/src/lib/utils/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/src/components/ui/popover';

const SORT_OPTIONS = [
  {
    direction: 'asc' as const,
    translationKey: 'lowToHigh',
  },
  {
    direction: 'desc' as const,
    translationKey: 'highToLow',
  },
] as const;

export const ProductFilter = React.memo(() => {
  const [open, setOpen] = useState(false);
  const searchParams = useSearchParams();
  const t = useTranslations('product.Filters');

  const { createQueryString, removeQueryString } = useNavigation({
    justUpdateUrl: true,
  });

  const currentSortBy = searchParams.get('sortBy') || searchParams.get('SortBy');
  const currentDirection = searchParams.get('direction');

  const isOffersActive = searchParams.get('haveOffers') === 'true';
  const isSortActive = currentSortBy === 'price';

  const selectedOption = SORT_OPTIONS.find((option) => option.direction === currentDirection);

  const sortTriggerLabel =
    isSortActive && selectedOption ? t(selectedOption.translationKey) : t('sort');

  const handleOffersToggle = () => {
    if (isOffersActive) {
      removeQueryString('haveOffers');
    } else {
      createQueryString({ haveOffers: 'true' });
    }
  };

  const handleSortSelect = (direction: 'asc' | 'desc') => {
    const isSameSelection = currentSortBy === 'price' && currentDirection === direction;

    if (isSameSelection) {
      removeQueryString(['sortBy', 'SortBy', 'direction', 'price']);
    } else {
      createQueryString({
        sortBy: 'price',
        direction,
      });
    }

    setOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleOffersToggle}
        className={cn(
          'relative flex cursor-pointer items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-300',
          isOffersActive
            ? 'bg-black text-white shadow-sm'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
        )}
      >
        <CirclePercent
          className={cn(
            'size-4 transition-colors',
            isOffersActive ? 'text-white' : 'text-rose-400',
          )}
        />
        <span>{t('offers')}</span>
      </button>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'relative flex cursor-pointer items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-300',
              isSortActive
                ? 'bg-black text-white shadow-sm'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            )}
          >
            <ArrowUpDown
              className={cn(
                'size-4 transition-colors',
                isSortActive ? 'text-white' : 'text-gray-600',
              )}
            />
            <span>{sortTriggerLabel}</span>
          </button>
        </PopoverTrigger>

        <PopoverContent className="w-48 p-2">
          <div className="flex flex-col gap-1">
            {SORT_OPTIONS.map(({ direction, translationKey }) => {
              const isSelected = isSortActive && currentDirection === direction;

              return (
                <button
                  key={direction}
                  type="button"
                  onClick={() => handleSortSelect(direction)}
                  className={cn(
                    'flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isSelected ? 'bg-black text-white' : 'text-gray-700 hover:bg-gray-100',
                  )}
                >
                  <span>{t(translationKey)}</span>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
});

ProductFilter.displayName = 'ProductFilter';
